import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import makeStyles from '@mui/styles/makeStyles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Adsense from 'components/Adsense/Adsense';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Container from 'components/Container/Container';
import { getImage } from 'utils/getImageLink';
import PublicationAbout from 'components/Publications/About';
import CardPostBig from 'components/Card/CardPostBig';
import NotFound from 'containers/NotFound/Loadable';
import MiddotDivider from 'components/MiddotDivider/MiddotDivider';
import config from 'config';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router';

const { domain } = config;

const useStyles = makeStyles(
  (theme) => ({
    noStoriesContainer: {
      marginTop: 180,
      marginBottom: 180,
    },
    containerGrid: {
      marginTop: 18,
      marginBottom: 30,
    },
    containerContentOverImg: {},
    '@media (max-width: 767px)': {
      containerContentOverImg: {
        marginLeft: -28,
        marginRight: -28,
      },
    },
    containerPlaceholder: {
      width: '100%',
      minHeight: 1,
    },
    sectionTitle: {
      lineHeight: 1.2,
      color: 'rgba(0,0,0,.68)',
      fontSize: 20,
      fontWeight: 400,
      borderBottom: '1px solid rgba(0,0,0,.05)',
      paddingBottom: 8,
    },
    sectiontitlePromo: {
      marginTop: 35,
      marginBottom: -20,
    },
    promoContainer: {
      textAlign: 'center',
      paddingTop: 35,
      paddingBottom: 20,
      paddingLeft: 20,
      paddingRight: 20,
      marginTop: 30,
      marginBottom: 15,
    },
    promoImageContainer: {},
    promoTextContainer: {
      maxWidth: 600,
      width: '100%',
    },
    promoTitle: {
      fontSize: 30,
      color: 'rgba(0,0,0,.54)',
      letterSpacing: 0,
      lineHeight: 1.3,
      fontWeight: 400,
    },
    adsenseInnerSections: {
      textAlign: 'center',
      marginTop: 20,
      marginBottom: 20,
      overflow: 'hidden',
    },
    containerFooter: {
      borderTop: '1px solid rgba(0,0,0,.15)',
      paddingBottom: 30,
      paddingTop: 10,
      marginTop: 10,
    },
    linkSet: {
      textAlign: 'right',
      marginTop: 10,
      '@media (max-width: 767px)': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    noPadding: {
      padding: 0,
    },
    tabsFlexContainer: {
      height: 48,
    },
    tabsRoot: {
      position: 'relative',
      borderBottom: '0',
      '& > $tabsFlexContainer': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      },
    },
    scrollButtons: {
      position: 'absolute',
      left: 0,
      top: -1,
      bottom: 1,
      zIndex: 9999,
      background: '#fff',
      width: 'auto',
    },
    tabsScroller: {
      position: 'relative',
      bottom: -1,
      display: 'flex',
      height: 60,
      overflowY: 'hidden',
      // '& $tabsFlexContainer': {
      //   borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
      // }
      '&+$scrollButtons': {
        left: 'auto',
        right: 0,
      },
    },
    tabsIndicator: {
      height: 1,
      top: 46,
      backgroundColor: 'rgba(0, 0, 0, 0.54)',
    },
    '@global': {
      '.no-hiddenscroll': {
        '& $tabsScroller': {
          bottom: -4,
          height: 61,
        },
        '& $tabsIndicator': {
          top: 43,
        },
      },
    },
    tabRoot: {
      fontSize: 15.8,
      fontFamily: [
        'wm-content-sans-serif-font',
        'Lucida Grande',
        'Lucida Sans Unicode',
        'Lucida Sans',
        'Geneva',
        'Arial',
        'sans-serif',
      ].join(','),
      textTransform: 'initial',
      minWidth: 72,
      fontWeight: theme.typography.fontWeightRegular,
      marginRight: theme.spacing(4),
      '&:hover': {
        color: 'rgba(0, 0, 0, 0.84)',
        opacity: 1,
      },
      '&$tabSelected': {
        color: 'rgba(0, 0, 0, 0.84)',
        fontWeight: theme.typography.fontWeightMedium,
      },
      '&:focus': {
        color: 'rgba(0, 0, 0, 0.84)',
      },
    },
    tabSelected: {},
    dotHidden: {
      display: 'inline-block',
      '@media (max-width: 767px)': {
        display: 'none',
      },
    },
  }),
  { name: 'WMPublicationLatest' }
);

const Latest = ({ type }: { type: 'latest' | 'trending' }) => {
  const classes = useStyles();
  const intl = useIntl();
  const params = useParams();
  const location = useLocation();
  const { publications, stories, loading, error } = useSelector((state) => {
    return {
      stories: (state as any).stories && (state as any).stories.stories,
      publications: (state as any).publications.publication,
      error: (state as any).publications.error,
      loading: (state as any).publications.loading,
    };
  });

  const publicationStream = publications[params.publication];

  const [tabValue, setTab] = useState(type === 'latest' ? 0 : 1);

  if (
    !loading &&
    (error || !publicationStream || !publicationStream[0] || !publicationStream[0].name)
  ) {
    return <NotFound error={error} />;
  }

  // @ts-expect-error ts-migrate(6133) FIXME: 'event' is declared but its value is never read.
  const handleChangeTab = (event: any, tab: any) => {
    setTab(tab);
  };

  const publication = publicationStream[0];
  const title =
    type === 'latest'
      ? `Latest stories published on ${publication.name} – WebMediums`
      : `Trending stories published on ${publication.name} – WebMediums`;
  const canonicalLink = `/${publication.slug}/${type}`;
  const { description } = publication;
  const descriptionThis =
    type === 'latest'
      ? `Read the latest stories published by ${publication.name}. ${description}`
      : `Read the trending stories published by ${publication.name}. ${description}`;
  const featureImageSrc =
    publication.logo && publication.logo.md5 && getImage(publication.logo, 'max_1200');
  const canonicaFeddlLink = `${domain}/feed/${publication.slug}`;

  const paramsSearch = {
    status: 'public',
    $skip: 0,
    $limit: 20,
    $paginate: false,
    $exclude: ['content'],
    $sort:
      type === 'latest'
        ? {
            publishDate: -1,
          }
        : {
            claps: -1,
            views: -1,
          },
    publication: params.publication,
  };

  const key = JSON.stringify(paramsSearch);
  const storiesCurrent = stories[key];

  const helmet = (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <link rel="canonical" href={canonicalLink} />
      <meta name="description" content={descriptionThis} />

      {featureImageSrc && <meta property="og:image" content={featureImageSrc} />}
      <meta property="og:title" content={title} />
      <meta property="twitter:title" content={title} />
      <meta property="og:url" content={canonicalLink} />
      <meta property="og:description" content={descriptionThis} />
      <meta name="twitter:description" content={descriptionThis} />

      {featureImageSrc && <meta name="twitter:image:src" content={featureImageSrc} />}

      {/* <link rel="author" href={canonicalLinkUser} /> */}
      {/* <meta property="author" content={publication ? publication.name : authorName} /> */}
      {/* <meta property="og:type" content="article" /> */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="robots" content="index, follow" />
      <link rel="alternate" type="application/rss+xml" title="RSS" href={canonicaFeddlLink} />
    </Helmet>
  );

  return (
    <>
      {helmet}
      {/* @ts-expect-error ts-migrate(2746) FIXME: This JSX tag's 'children' prop expects a single ch... Remove this comment to see the full error message */}
      <Container size="publicationSize">
        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <Tabs
                  variant="scrollable"
                  scrollButtons
                  value={tabValue}
                  onChange={handleChangeTab}
                  classes={{
                    scrollButtons: classes.scrollButtons,
                    root: classes.tabsRoot,
                    indicator: classes.tabsIndicator,
                    scroller: classes.tabsScroller,
                    flexContainer: classes.tabsFlexContainer,
                  }}
                  allowScrollButtonsMobile
                >
                  <Tab
                    component={Link}
                    to={`/${publication.slug}/latest`}
                    disableRipple
                    classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
                    label="Latest"
                  />
                  <Tab
                    component={Link}
                    to={`/${publication.slug}/trending`}
                    disableRipple
                    classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
                    label="Trending"
                  />
                </Tabs>
              </Grid>
              {storiesCurrent &&
                storiesCurrent.map((story: any) => (
                  <Grid key={`story-in-pub-${story.uid}`} item xs={12}>
                    {/* @ts-expect-error ts-migrate(2741) FIXME: Property 'publication' is missing in type '{ post:... Remove this comment to see the full error message */}
                    <CardPostBig post={story} />
                  </Grid>
                ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PublicationAbout publication={publication} />
              </Grid>
              <Grid item xs={12}>
                <Adsense
                  key={`${location.key}-adsense-publications-sidebar`}
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ key: string; responsive: string; client: s... Remove this comment to see the full error message
                  responsive="true"
                  client="ca-pub-9169771209644523"
                  slot="8212156070"
                  format="vertical"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container className={classes.containerFooter}>
          <Grid item xs={12} className={classes.linkSet}>
            <Button
              className={classes.noPadding}
              component={Link}
              to={`/${publication.slug}/about`}
            >
              About {publication.name}
            </Button>
            <div className={classes.dotHidden}>
              <MiddotDivider />
            </div>
            <Button
              className={classes.noPadding}
              component={Link}
              to={`/${publication.slug}/latest`}
            >
              Latest Stories
            </Button>
            <div className={classes.dotHidden}>
              <MiddotDivider />
            </div>
            <Button
              className={classes.noPadding}
              component={Link}
              to={`/${publication.slug}/archive`}
            >
              Archive
            </Button>
            <div className={classes.dotHidden}>
              <MiddotDivider />
            </div>
            <Button
              className={classes.noPadding}
              component={Link}
              to="/policy/politica-de-privacidad-de-webmediums-nkgyas2objom"
            >
              {intl.formatMessage({
                id: 'terms',
                defaultMessage: 'Terms',
              })}
            </Button>
            <div className={classes.dotHidden}>
              <MiddotDivider />
            </div>
            <Button
              className={classes.noPadding}
              component={Link}
              to="/policy/politica-de-privacidad-de-webmediums-nkgyas2objom"
            >
              {intl.formatMessage({
                id: 'privacy',
                defaultMessage: 'Privacy',
              })}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default Latest;
